/**
 * Prijsafspraken per klant — beheer-overzicht voor de Offerte Generator.
 * De gebruiker legt hier per klant de vaste afspraken vast (bv. Narrative Labs /
 * ICS = €500 per video). De generator injecteert de actieve afspraak in de prompt,
 * zodat het concept de klant-specifieke prijzen aanhoudt.
 *
 * Module-level component (niet binnen een parent gedefinieerd) i.v.m. de bekende
 * React remount-bug bij inline gedefinieerde componenten.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Stack, Chip, Switch,
  FormControlLabel, Divider, CircularProgress, Alert, Autocomplete, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';

const EMPTY = { id: null, company_name: '', gripp_company_id: null, agreements: '', active: true };

export default function KlantAfspraken({ userEmail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // het record dat bewerkt wordt (of EMPTY voor nieuw)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Gripp-bedrijf zoeken (om te koppelen)
  const [companyOpts, setCompanyOpts] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    authFetch(`${AI_ENGINE_URL}/api/v1/client-agreements`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setError('Afspraken laden mislukt'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const searchCompanies = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!q || q.length < 2) return;
      const seq = ++seqRef.current;
      setCompanyLoading(true);
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/offertes/companies?q=${encodeURIComponent(q)}`);
        const data = await leesJson(res, 'Gripp-bedrijven zoeken');
        if (seq === seqRef.current) setCompanyOpts(data.companies || []);
      } catch (e) { setError(e.message || 'Gripp-bedrijven zoeken mislukt'); } finally { if (seq === seqRef.current) setCompanyLoading(false); }
    }, 300);
  }, []);

  const save = async () => {
    if (!edit?.company_name?.trim()) { setError('Klantnaam is verplicht'); return; }
    setSaving(true); setError(null);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/client-agreements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...edit, created_by: userEmail }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Opslaan mislukt');
      setEdit(null);
      load();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await bevestig({ titel: 'Prijsafspraak verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/client-agreements/${id}`, { method: 'DELETE' });
      load();
    } catch { setError('Verwijderen mislukt'); }
  };

  const toggleActive = async (it) => {
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/client-agreements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: it.id, company_name: it.company_name, active: !it.active }),
      });
      load();
    } catch { setError('Bijwerken mislukt'); }
  };

  return (
    <Paper sx={{ p: 2, border: '1px solid #e5e7eb' }} elevation={0}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <HandshakeIcon sx={{ color: '#0ea5e9' }} fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>Prijsafspraken per klant</Typography>
        <Box sx={{ flex: 1 }} />
        {!edit && <Button size="small" startIcon={<AddIcon />} onClick={() => setEdit({ ...EMPTY })}>Nieuwe afspraak</Button>}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Vaste afspraken (bv. "€500 per video") worden automatisch in de offerte-generator meegenomen zodra je de klant kiest.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Bewerk-/nieuw-formulier */}
      {edit && (
        <Box sx={{ p: 1.5, mb: 1.5, border: '1px solid #c7d2fe', borderRadius: 1, bgcolor: '#fafaff' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField size="small" label="Klantnaam" sx={{ flex: 1 }} value={edit.company_name}
              onChange={(e) => setEdit(p => ({ ...p, company_name: e.target.value }))} />
            <Autocomplete
              size="small" sx={{ flex: 1, minWidth: 220 }} options={companyOpts}
              getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={companyLoading} filterOptions={(x) => x}
              value={edit.gripp_company_id ? { id: edit.gripp_company_id, name: edit.company_name } : null}
              onInputChange={(_e, val, reason) => { if (reason === 'input') searchCompanies(val); }}
              onChange={(_e, val) => setEdit(p => ({ ...p, gripp_company_id: val?.id || null, company_name: val?.name || p.company_name }))}
              renderInput={(params) => <TextField {...params} label="Koppel Gripp-bedrijf (optioneel)" placeholder="Zoek in Gripp…" />}
              noOptionsText="Typ om te zoeken"
            />
          </Stack>
          <TextField fullWidth multiline minRows={3} size="small" label="Afspraken"
            placeholder={'Bv.:\n- Standaardtarief €500 per video\n- Minimaal 4 video\'s per opdracht\n- Reiskosten inbegrepen'}
            value={edit.agreements} onChange={(e) => setEdit(p => ({ ...p, agreements: e.target.value }))} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
            <FormControlLabel control={<Switch size="small" checked={!!edit.active} onChange={(e) => setEdit(p => ({ ...p, active: e.target.checked }))} />} label="Actief" />
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => { setEdit(null); setError(null); }}>Annuleren</Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Lijst */}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', py: 2 }}>
          <CircularProgress size={16} /><Typography variant="caption">Laden…</Typography>
        </Box>
      ) : items.length === 0 && !edit ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nog geen prijsafspraken vastgelegd.</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {items.map((it) => (
            <Box key={it.id} sx={{ py: 1, opacity: it.active ? 1 : 0.55 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={700}>{it.company_name}</Typography>
                    {it.gripp_company_id && <Chip size="small" label="Gripp" sx={{ height: 18, fontSize: 10 }} />}
                    {!it.active && <Chip size="small" label="inactief" sx={{ height: 18, fontSize: 10 }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', display: 'block', mt: 0.25 }}>
                    {it.agreements || '—'}
                  </Typography>
                </Box>
                <Tooltip title={it.active ? 'Op inactief zetten' : 'Activeren'}>
                  <Switch size="small" checked={!!it.active} onChange={() => toggleActive(it)} />
                </Tooltip>
                <IconButton size="small" onClick={() => setEdit({ ...it })}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(it.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
